import {
  REPORT_RENDERER_VERSION,
  REPORT_SCHEMA_VERSION,
  getSourceModuleForReportType,
  normalizeReportOrientation,
} from "./reportTypes";

const ACTIVE_WORKSPACE_PREFIX = "reports:activeWorkspace";
const REPORT_WORKSPACE_EVENT = "electionmap:reports-workspace-changed";

function normalizeScopeValue(value) {
  return String(value || "").trim();
}

function safeClone(value, fallback = null) {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (_) {
    return fallback;
  }
}

export function getActiveWorkspaceStorageKey(userId, areaId) {
  const userKey = normalizeScopeValue(userId);
  const areaKey = normalizeScopeValue(areaId);
  if (!userKey || !areaKey) return "";
  return `${ACTIVE_WORKSPACE_PREFIX}:${userKey}:${areaKey}`;
}

export function readActiveWorkspaceId(userId, areaId) {
  const storageKey = getActiveWorkspaceStorageKey(userId, areaId);
  if (!storageKey || typeof window === "undefined" || !window.localStorage) return "";
  try {
    return String(window.localStorage.getItem(storageKey) || "").trim();
  } catch (_) {
    return "";
  }
}

export function writeActiveWorkspaceId(userId, areaId, workspaceId) {
  const storageKey = getActiveWorkspaceStorageKey(userId, areaId);
  if (!storageKey || typeof window === "undefined" || !window.localStorage) return;
  try {
    const normalized = normalizeScopeValue(workspaceId);
    if (normalized) {
      window.localStorage.setItem(storageKey, normalized);
    } else {
      window.localStorage.removeItem(storageKey);
    }
  } catch (_) {
    // Preference storage is best-effort only.
  }
}

export function emitReportWorkspaceChanged(detail = {}) {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(REPORT_WORKSPACE_EVENT, { detail }));
  } catch (_) {
    window.dispatchEvent(new Event(REPORT_WORKSPACE_EVENT));
  }
}

export function subscribeReportWorkspaceChanged(callback) {
  if (typeof window === "undefined") return () => {};
  const onWorkspaceChanged = () => callback && callback();
  window.addEventListener(REPORT_WORKSPACE_EVENT, onWorkspaceChanged);
  return () => window.removeEventListener(REPORT_WORKSPACE_EVENT, onWorkspaceChanged);
}

function normalizeItem(item, index = 0) {
  if (!item || typeof item !== "object") return null;
  const type = String(item.type || item.source_type || "").trim();
  const sourceModule = String(item.sourceModule || item.source_module || getSourceModuleForReportType(type) || "").trim();
  const snapshot = item.snapshot && typeof item.snapshot === "object" ? safeClone(item.snapshot, {}) : {};

  return {
    id: item.id == null ? "" : String(item.id),
    schemaVersion: Number(item.schemaVersion) || REPORT_SCHEMA_VERSION,
    rendererVersion: String(item.rendererVersion || REPORT_RENDERER_VERSION),
    type,
    sourceModule,
    areaId: item.areaId,
    areaName: String(item.areaName || ""),
    title: String(item.title || "Report"),
    note: String(item.note || ""),
    orientation: normalizeReportOrientation(type, item.orientation),
    includeInExport: item.includeInExport !== false,
    sortOrder: Number.isFinite(Number(item.sortOrder)) ? Number(item.sortOrder) : index,
    createdAt: String(item.createdAt || new Date().toISOString()),
    updatedAt: String(item.updatedAt || item.createdAt || new Date().toISOString()),
    snapshot,
    preview: item.preview && typeof item.preview === "object" ? safeClone(item.preview, null) : null,
  };
}

export function normalizeReportItems(items) {
  return (Array.isArray(items) ? items : [])
    .map(normalizeItem)
    .filter(Boolean)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((item, index) => ({ ...item, sortOrder: index }));
}

export function createReportItem({ type, sourceModule, areaId, areaName, title, note, orientation, snapshot, preview }) {
  const now = new Date().toISOString();
  const id =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `report-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return normalizeItem(
    {
      id,
      schemaVersion: REPORT_SCHEMA_VERSION,
      rendererVersion: REPORT_RENDERER_VERSION,
      type,
      sourceModule: sourceModule || getSourceModuleForReportType(type),
      areaId,
      areaName,
      title,
      note,
      orientation: normalizeReportOrientation(type, orientation),
      includeInExport: true,
      sortOrder: 0,
      createdAt: now,
      updatedAt: now,
      snapshot: safeClone(snapshot, {}),
      preview,
    },
    0
  );
}
